import { useEffect, useMemo, useState } from "react";
import { useNavigate } from "react-router-dom";
import {
  Plus, Trash2, Loader2, Save, ArrowLeft, Wrench, Package, Sparkles,
  Check, TriangleAlert, X, ChevronDown, ChevronRight,
} from "lucide-react";
import api, { money } from "@/lib/api";
import { useAuth } from "@/context/AuthContext";
import Layout from "@/components/Layout";
import { Button } from "@/components/ui/button";
import { Input } from "@/components/ui/input";
import { Label } from "@/components/ui/label";
import { Textarea } from "@/components/ui/textarea";
import {
  Select, SelectContent, SelectItem, SelectTrigger, SelectValue,
} from "@/components/ui/select";
import { toast } from "sonner";

let _seq = 0;
const uid = () => `hi_${Date.now()}_${_seq++}`;

// Standard vehicle inspection template (mirrors the mobile app's guided checklist)
const TEMPLATE = [
  { system: "Engine", items: ["Oil level & condition", "Belts & hoses", "Leaks / gaskets", "Engine air filter"] },
  { system: "Brakes", items: ["Front pads & rotors", "Rear pads & rotors", "Brake fluid", "Brake lines & hoses"] },
  { system: "Tires", items: ["Tread depth", "Tire pressure", "Wear pattern", "Spare tire"] },
  { system: "Fluids", items: ["Coolant", "Transmission fluid", "Power steering fluid", "Washer fluid"] },
  { system: "Battery/Electrical", items: ["Battery health", "Charging system", "Terminals & cables", "Exterior lights"] },
  { system: "Suspension", items: ["Shocks & struts", "Ball joints", "Tie rods", "Wheel alignment"] },
  { system: "Belts & Hoses", items: ["Serpentine belt", "Radiator hoses", "Vacuum lines"] },
  { system: "HVAC", items: ["A/C performance", "Cabin air filter", "Heater operation"] },
];

const STATUS = [
  { value: "good", label: "Good", Icon: Check, active: "bg-emerald-500 border-emerald-500 text-white", idle: "border-emerald-500/40 text-emerald-400", ring: "text-emerald-400" },
  { value: "attention", label: "Attention", Icon: TriangleAlert, active: "bg-amber-500 border-amber-500 text-slate-950", idle: "border-amber-500/40 text-amber-400", ring: "text-amber-400" },
  { value: "critical", label: "Critical", Icon: X, active: "bg-rose-600 border-rose-600 text-white", idle: "border-rose-500/40 text-rose-400", ring: "text-rose-400" },
];

const buildTemplateItems = () =>
  TEMPLATE.flatMap((sec) => sec.items.map((label) => ({
    _key: uid(), system: sec.system, label, status: "good", note: "", part_name: "", part_cost: "", labor_hours: "",
  })));

export default function ReportEditor() {
  const navigate = useNavigate();
  const { user } = useAuth();
  const laborRate = user?.labor_rate || 100;
  const [customers, setCustomers] = useState([]);
  const [saving, setSaving] = useState(false);
  const [genLoading, setGenLoading] = useState(false);
  const [form, setForm] = useState({
    customer_name: "", customer_business_name: "", customer_business_address: "",
    vehicle_make: "", vehicle_model: "", vehicle_year: "", vehicle_vin: "", mileage: "",
    summary: "", items: buildTemplateItems(),
  });
  const [collapsed, setCollapsed] = useState({});
  const set = (k, v) => setForm((f) => ({ ...f, [k]: v }));

  useEffect(() => { api.get("/customers").then(({ data }) => setCustomers(data || [])).catch(() => {}); }, []);

  const pickCustomer = (cid) => {
    const c = customers.find((x) => x.id === cid);
    if (!c) return;
    setForm((f) => ({
      ...f, customer_name: c.name || "", customer_business_name: c.business_name || "", customer_business_address: c.business_address || "",
      vehicle_make: c.vehicle_make || "", vehicle_model: c.vehicle_model || "", vehicle_year: c.vehicle_year || "", vehicle_vin: c.vehicle_vin || "",
    }));
    toast.success(`Loaded ${c.name}`);
  };

  const updateItem = (key, k, v) => setForm((f) => ({ ...f, items: f.items.map((it) => (it._key === key ? { ...it, [k]: v } : it)) }));
  const removeItem = (key) => setForm((f) => ({ ...f, items: f.items.filter((it) => it._key !== key) }));
  const addItem = (system) => setForm((f) => ({ ...f, items: [...f.items, { _key: uid(), system, label: "", status: "good", note: "", part_name: "", part_cost: "", labor_hours: "" }] }));

  const systemsInOrder = useMemo(() => [...new Set(form.items.map((i) => i.system))], [form.items]);

  const counts = useMemo(() => {
    const c = { good: 0, attention: 0, critical: 0 };
    form.items.forEach((i) => { if (c[i.status] !== undefined) c[i.status]++; });
    return c;
  }, [form.items]);

  const recommendedTotal = useMemo(() => form.items.reduce(
    (s, it) => s + Number(it.part_cost || 0) + Number(it.labor_hours || 0) * laborRate, 0), [form.items, laborRate]);

  const generateSummary = async () => {
    const labeled = form.items.filter((it) => it.label.trim());
    if (labeled.length === 0) return toast.error("Add at least one inspection item first");
    const keywords = labeled.map((it) => `${it.label} (${it.status})${it.note ? `: ${it.note}` : ""}`).join("; ");
    setGenLoading(true);
    try {
      const { data } = await api.post("/ai/generate", { mode: "summary", keywords });
      set("summary", data.text || "");
      toast.success("Summary generated");
    } catch (e) {
      toast.error(e?.response?.data?.detail || "Could not generate summary");
    } finally { setGenLoading(false); }
  };

  const save = async () => {
    if (!form.customer_name.trim()) return toast.error("Customer name is required");
    setSaving(true);
    const payload = {
      ...form,
      items: form.items.filter((it) => it.label.trim()).map((it) => ({
        system: it.system, label: it.label, status: it.status, note: it.note,
        part_name: it.part_name || null,
        part_cost: it.part_cost === "" || it.part_cost == null ? null : Number(it.part_cost),
        labor_hours: it.labor_hours === "" || it.labor_hours == null ? null : Number(it.labor_hours),
      })),
    };
    try {
      const { data } = await api.post("/reports", payload);
      toast.success("Health report saved");
      toast.dismiss();
      navigate(`/reports/${data.id}`);
    } catch (e) { toast.error(e?.response?.data?.detail || "Failed to save"); }
    finally { setSaving(false); }
  };

  const field = "h-10 bg-slate-950 border-slate-700 focus-visible:ring-amber-500/50";

  return (
    <Layout
      title="New Health Report"
      action={
        <div className="flex gap-2">
          <Button variant="outline" data-testid="cancel-report" onClick={() => navigate(-1)} className="bg-slate-900 border-slate-700 text-slate-300 hover:bg-slate-800"><ArrowLeft className="h-4 w-4 mr-1.5" /> Back</Button>
          <Button data-testid="save-report-button" onClick={save} disabled={saving} className="bg-amber-500 hover:bg-amber-400 text-slate-950 font-semibold">{saving ? <Loader2 className="h-4 w-4 mr-1.5 animate-spin" /> : <Save className="h-4 w-4 mr-1.5" />} Save Report</Button>
        </div>
      }
    >
      <div className="grid grid-cols-1 xl:grid-cols-3 gap-6 animate-fade-up">
        {/* LEFT: customer + inspection */}
        <div className="xl:col-span-2 space-y-6">
          <section className="rounded-xl border border-slate-800 bg-slate-900 p-5">
            <div className="flex items-center justify-between mb-4">
              <h3 className="font-head font-bold text-slate-100">Customer & Vehicle</h3>
              {customers.length > 0 && (
                <Select onValueChange={pickCustomer}>
                  <SelectTrigger data-testid="report-customer-picker" className="h-9 w-56 bg-slate-950 border-slate-700"><SelectValue placeholder="Quick-fill customer…" /></SelectTrigger>
                  <SelectContent className="bg-slate-900 border-slate-800 text-slate-200 max-h-64">
                    {customers.map((c) => (<SelectItem key={c.id} value={c.id}>{c.name}</SelectItem>))}
                  </SelectContent>
                </Select>
              )}
            </div>
            <div className="grid grid-cols-2 sm:grid-cols-4 gap-3">
              <div className="space-y-1.5 col-span-2"><Label className="text-slate-300 text-xs">Customer name *</Label><Input data-testid="report-customer-name" value={form.customer_name} onChange={(e) => set("customer_name", e.target.value)} className={field} /></div>
              <div className="space-y-1.5"><Label className="text-slate-300 text-xs">Mileage</Label><Input value={form.mileage} onChange={(e) => set("mileage", e.target.value)} className={field} /></div>
              <div className="space-y-1.5"><Label className="text-slate-300 text-xs">Year</Label><Input value={form.vehicle_year} onChange={(e) => set("vehicle_year", e.target.value)} className={field} /></div>
              <div className="space-y-1.5"><Label className="text-slate-300 text-xs">Make</Label><Input value={form.vehicle_make} onChange={(e) => set("vehicle_make", e.target.value)} className={field} /></div>
              <div className="space-y-1.5"><Label className="text-slate-300 text-xs">Model</Label><Input value={form.vehicle_model} onChange={(e) => set("vehicle_model", e.target.value)} className={field} /></div>
              <div className="space-y-1.5 col-span-2"><Label className="text-slate-300 text-xs">VIN</Label><Input value={form.vehicle_vin} onChange={(e) => set("vehicle_vin", e.target.value)} className={field} /></div>
            </div>
          </section>

          <section className="rounded-xl border border-slate-800 bg-slate-900 p-5" data-testid="vehicle-inspection">
            <p className="text-[11px] uppercase tracking-widest text-slate-500 font-semibold mb-4">Vehicle Inspection</p>

            <div className="space-y-4">
              {systemsInOrder.map((system) => {
                const items = form.items.filter((it) => it.system === system);
                const isCollapsed = collapsed[system];
                return (
                  <div key={system} data-testid={`report-section-${system}`}>
                    <button
                      type="button"
                      data-testid={`section-toggle-${system}`}
                      onClick={() => setCollapsed((c) => ({ ...c, [system]: !c[system] }))}
                      className="w-full flex items-center gap-2 mb-2 group"
                    >
                      {isCollapsed ? <ChevronRight className="h-4 w-4 text-slate-500" /> : <ChevronDown className="h-4 w-4 text-slate-500" />}
                      <span className="font-head font-bold text-amber-500 tracking-wide">{system}</span>
                      <span className="text-[11px] text-slate-600 ml-1">{items.length}</span>
                    </button>

                    {!isCollapsed && (
                      <div className="space-y-2.5 pl-1">
                        {items.map((it) => {
                          const flagged = it.status !== "good";
                          return (
                            <div key={it._key} data-testid={`inspection-item-${it._key}`} className="rounded-lg border border-slate-800 bg-slate-950/60 p-3">
                              <div className="flex items-center justify-between gap-3">
                                {it.label && !it._custom ? (
                                  <p className="text-sm font-medium text-slate-100 flex-1 min-w-0">{it.label}</p>
                                ) : (
                                  <Input data-testid={`inspection-label-${it._key}`} value={it.label} onChange={(e) => updateItem(it._key, "label", e.target.value)} placeholder="Item name" className="h-9 flex-1 bg-slate-950 border-slate-700 focus-visible:ring-amber-500/50" />
                                )}
                                <div className="flex items-center gap-2 shrink-0">
                                  {STATUS.map((s) => {
                                    const selected = it.status === s.value;
                                    const Icon = s.Icon;
                                    return (
                                      <button
                                        key={s.value}
                                        type="button"
                                        data-testid={`inspection-status-${it._key}-${s.value}`}
                                        onClick={() => updateItem(it._key, "status", s.value)}
                                        aria-label={s.label}
                                        className={`h-9 w-9 rounded-md border flex items-center justify-center transition-colors duration-150 ${selected ? s.active : `bg-slate-900 ${s.idle} opacity-80 hover:opacity-100 hover:bg-slate-800`}`}
                                      >
                                        <Icon className="h-4 w-4" strokeWidth={2.5} />
                                      </button>
                                    );
                                  })}
                                  <Button variant="ghost" size="icon" data-testid={`remove-inspection-${it._key}`} onClick={() => removeItem(it._key)} className="h-8 w-8 text-slate-600 hover:text-rose-400"><Trash2 className="h-4 w-4" /></Button>
                                </div>
                              </div>

                              {flagged && (
                                <div className="mt-3 pt-3 border-t border-slate-800 grid grid-cols-12 gap-2 items-center" data-testid={`item-fix-${it._key}`}>
                                  <span className="col-span-12 sm:col-span-2 text-[11px] uppercase tracking-wide text-slate-500 font-semibold flex items-center gap-1"><Package className="h-3.5 w-3.5 text-slate-600" /> Recommend</span>
                                  <Input data-testid={`inspection-note-${it._key}`} value={it.note} onChange={(e) => updateItem(it._key, "note", e.target.value)} placeholder="Note (e.g. 3mm left)" className="col-span-12 sm:col-span-4 h-9 bg-slate-950 border-slate-700 focus-visible:ring-amber-500/50" />
                                  <Input data-testid={`inspection-part-name-${it._key}`} value={it.part_name} onChange={(e) => updateItem(it._key, "part_name", e.target.value)} placeholder="Part" className="col-span-6 sm:col-span-3 h-9 bg-slate-950 border-slate-700 focus-visible:ring-amber-500/50" />
                                  <div className="col-span-3 sm:col-span-2 relative">
                                    <span className="absolute left-2.5 top-1/2 -translate-y-1/2 text-slate-500 text-sm">$</span>
                                    <Input data-testid={`inspection-part-cost-${it._key}`} type="number" step="0.01" value={it.part_cost} onChange={(e) => updateItem(it._key, "part_cost", e.target.value)} placeholder="Cost" className="pl-5 h-9 bg-slate-950 border-slate-700 text-right focus-visible:ring-amber-500/50" />
                                  </div>
                                  <div className="col-span-3 sm:col-span-1 relative">
                                    <Input data-testid={`inspection-labor-hours-${it._key}`} type="number" step="0.1" value={it.labor_hours} onChange={(e) => updateItem(it._key, "labor_hours", e.target.value)} placeholder="hrs" className="h-9 bg-slate-950 border-slate-700 text-right pr-7 focus-visible:ring-amber-500/50" />
                                    <span className="absolute right-2 top-1/2 -translate-y-1/2 text-slate-500 text-[10px] pointer-events-none">hrs</span>
                                  </div>
                                </div>
                              )}
                            </div>
                          );
                        })}
                        <Button size="sm" variant="ghost" data-testid={`add-item-${system}`} onClick={() => addItem(system)} className="h-8 text-amber-400 hover:text-amber-300 hover:bg-slate-800"><Plus className="h-3.5 w-3.5 mr-1" /> Add item</Button>
                      </div>
                    )}
                  </div>
                );
              })}
            </div>
          </section>
        </div>

        {/* RIGHT: summary rail */}
        <div className="xl:col-span-1">
          <div className="rounded-xl border border-slate-800 bg-slate-900 p-5 sticky top-24 space-y-5">
            <div>
              <h3 className="font-head font-bold text-slate-100 mb-3">Inspection Summary</h3>
              <div className="grid grid-cols-3 gap-2">
                {STATUS.map((s) => (
                  <div key={s.value} className="rounded-lg border border-slate-800 bg-slate-950/60 p-3 text-center" data-testid={`count-${s.value}`}>
                    <p className={`font-head font-black text-2xl ${s.ring}`}>{counts[s.value]}</p>
                    <p className="text-[10px] uppercase tracking-wide text-slate-500 mt-0.5">{s.label}</p>
                  </div>
                ))}
              </div>
            </div>

            {recommendedTotal > 0 && (
              <div className="rounded-lg border border-amber-500/20 bg-amber-500/5 p-4 flex items-center justify-between" data-testid="report-recommended-total">
                <span className="text-xs text-slate-400 flex items-center gap-1.5"><Wrench className="h-3.5 w-3.5 text-amber-400" /> Recommended work</span>
                <span className="font-head font-bold text-amber-400 tabular-nums">{money(recommendedTotal)}</span>
              </div>
            )}

            <div>
              <div className="flex items-center justify-between mb-1.5">
                <Label className="text-slate-300 text-xs">Technician summary</Label>
                <Button size="sm" variant="ghost" data-testid="generate-summary-button" onClick={generateSummary} disabled={genLoading} className="h-7 px-2 text-xs text-amber-400 hover:text-amber-300">
                  {genLoading ? <Loader2 className="h-3.5 w-3.5 mr-1 animate-spin" /> : <Sparkles className="h-3.5 w-3.5 mr-1" />} Generate with AI
                </Button>
              </div>
              <Textarea data-testid="report-summary" value={form.summary} onChange={(e) => set("summary", e.target.value)} placeholder="Overall condition and recommendations…" rows={5} className="bg-slate-950 border-slate-700 focus-visible:ring-amber-500/50" />
            </div>
          </div>
        </div>
      </div>
    </Layout>
  );
}
